import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AddPart.css';

const apiBaseURL = (process.env.REACT_APP_API_BASE_URL || 'https://api.vehiclesolution.co').replace(/\/+$/, '');

function AddPart() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  const today = now.toISOString().slice(0, 16);
  const [form, setForm] = useState({
    parts_name: '',
    Part_no: '',
    part_description: '',
    vendor_name: '',
    opening_date: today,
    opening_qty: '',
    unit: '',
    Location: '',
    Part_type: '',
    Brand: '',
    Limit: '',
    Dflag: false
  });
  const [partNoFeedback, setPartNoFeedback] = useState("");
  const [partNoDuplicate, setPartNoDuplicate] = useState(false);

  useEffect(() => {
    fetch(`${apiBaseURL}/api/manufacturers`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setManufacturers(data.manufacturers);
      });
  }, []);

  useEffect(() => {
    const trimmedVendorSearch = vendorSearchTerm.trim();

    if (!trimmedVendorSearch) {
      setVendors([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchVendors(trimmedVendorSearch);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [vendorSearchTerm]);

  const fetchVendors = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseURL}/api/vendors?search=${encodeURIComponent(query)}&page=1&pageSize=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setVendors(data.vendors || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleVendorSelect = (vendor) => {
    setForm((prev) => ({
      ...prev,
      vendor_name: vendor.company || ''
    }));
    setVendorSearchTerm(vendor.company || '');
    setVendors([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Remove spaces from Part_no before saving
      const formData = {
        ...form,
        Part_no: form.Part_no ? form.Part_no.replace(/\s+/g, '') : ''
      };
      
      const response = await fetch(`${apiBaseURL}/api/parts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        alert('Part added successfully!');
        navigate('/inventory/parts');
      } else {
        alert('Error adding part: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error: ' + error.message);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Brand-specific part number validation
  function validatePartNumber(brand, partNo) {
    if (!brand || !partNo) return { valid: true, normalized: partNo };
    const patterns = {
      // Japanese
      'Toyota': { regex: /^\d{5}-\d{5}$/i, example: '12345-12345', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{5})/, '$1-$2') },
      'Lexus': { regex: /^\d{5}-\d{5}$/i, example: '12345-12345', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{5})/, '$1-$2') },
      'Honda': { regex: /^\d{5}-\d{3}-\d{3}$/i, example: '12345-123-123', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{3})(\d{3})/, '$1-$2-$3') },
      'Nissan': { regex: /^\d{5}-\d{5}$/i, example: '12345-12345', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{5})/, '$1-$2') },
      'Mazda': { regex: /^(\d{5}-\d{2}-\d{3}|\d{5}-\d{3}-\d{3})$/i, example: '12345-12-123 or 12345-123-123', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{2,3})(\d{3})/, (m, a, b, c) => b.length === 2 ? `${a}-${b}-${c}` : `${a}-${b}-${c}`) },
      'Subaru': { regex: /^(\d{8}|\d{5}[A-Z]{2}\d{3})$/i, example: '12345678 or 12345AA123', normalize: v => v.replace(/[^\dA-Z]/gi, '') },
      'Suzuki': { regex: /^\d{5}-\d{5}$/i, example: '12345-12345', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{5})/, '$1-$2') },
      'Mitsubishi': { regex: /^(\d{5}[A-Z]\d{3}|MR\d{6})$/i, example: '4605A261 or MR527674', normalize: v => v.toUpperCase() },
      // Korean
      'Hyundai': { regex: /^\d{5}-\d{5}$/i, example: '58101-2PA70', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{5})([A-Z0-9]{5})/, '$1-$2') },
      'Kia': { regex: /^\d{5}-\d{5}$/i, example: '58101-2PA70', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{5})([A-Z0-9]{5})/, '$1-$2') },
      'Ssangyong': { regex: /^\d{10}$/i, example: '4145009000', normalize: v => v.replace(/[^\d]/g, '') },
      // European - VAG Group
      'Volkswagen': { regex: /^\d{3} \d{3} \d{3} ?[A-Z]?$/i, example: '1K0 805 903 J', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{3})(\d{3})(\d{3})([A-Z]?)/, '$1 $2 $3 $4').trim() },
      'Audi': { regex: /^\d{3} \d{3} \d{3} ?[A-Z]?$/i, example: '06H 121 026 DD', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{3})(\d{3})(\d{3})([A-Z]?)/, '$1 $2 $3 $4').trim() },
      'Skoda': { regex: /^\d{3} \d{3} \d{3} ?[A-Z]?$/i, example: '1K0 805 903 J', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{3})(\d{3})(\d{3})([A-Z]?)/, '$1 $2 $3 $4').trim() },
      'Bentley': { regex: /^\d{3} \d{3} \d{3} ?[A-Z]?$/i, example: '3W0 698 451', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{3})(\d{3})(\d{3})([A-Z]?)/, '$1 $2 $3 $4').trim() },
      'Porsche': { regex: /^\d{3} \d{3} \d{3} \d{2}$/i, example: '997 351 939 00', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1 $2 $3 $4') },
      // European - Other
      'Mercedes-Benz': { regex: /^A \d{3} \d{3} \d{2} \d{2}$/i, example: 'A 000 420 12 20', normalize: v => v.replace(/[^\dA]/gi, '').replace(/A(\d{3})(\d{3})(\d{2})(\d{2})/, 'A $1 $2 $3 $4') },
      'BMW': { regex: /^\d{2} \d{2} \d \d{3} \d{3}$/i, example: '34 11 6 792 251', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{2})(\d{2})(\d)(\d{3})(\d{3})/, '$1 $2 $3 $4 $5') },
      'Mini': { regex: /^\d{2} \d{2} \d \d{3} \d{3}$/i, example: '34 11 6 792 251', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{2})(\d{2})(\d)(\d{3})(\d{3})/, '$1 $2 $3 $4 $5') },
      'Mini Cooper': { regex: /^\d{2} \d{2} \d \d{3} \d{3}$/i, example: '34 11 6 792 251', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{2})(\d{2})(\d)(\d{3})(\d{3})/, '$1 $2 $3 $4 $5') },
      'Rolls-Royce': { regex: /^\d{2} \d{2} \d \d{3} \d{3}$/i, example: '51 11 7 123 456', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{2})(\d{2})(\d)(\d{3})(\d{3})/, '$1 $2 $3 $4 $5') },
      'Volvo': { regex: /^\d{7,8}$/i, example: '31445990', normalize: v => v.replace(/[^\d]/g, '') },
      'Jaguar': { regex: /^[A-Z]{2}\d{5,8}$/i, example: 'C2D23131', normalize: v => v.toUpperCase().replace(/[^A-Z\d]/g, '') },
      'Land Rover': { regex: /^LR\d{6,}$/i, example: 'LR012345', normalize: v => v.toUpperCase().replace(/[^A-Z\d]/g, '') },
      // American
      'Ford': { regex: /^\d{4}-\d{5}-[A-Z]$/i, example: 'FL3Z-2C026-A', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{4})-(\d{5})([A-Z])/, '$1-$2-$3') },
      'Jeep': { regex: /^\d{8}[A-Z]{2}$/i, example: '68212345AA', normalize: v => v.replace(/[^\dA-Z]/gi, '') },
      'Chevrolet': { regex: /^\d{8}$/i, example: '12612345', normalize: v => v.replace(/[^\d]/g, '') },
      // Chinese
      'Chery': { regex: /^[A-Z]\d{2}-\d{5,}$/i, example: 'A11-3501080', normalize: v => v.replace(/[^\dA-Z]/gi, '').toUpperCase() },
      'BYD': { regex: /^BYD\d+$/i, example: 'BYD12345', normalize: v => v.toUpperCase().replace(/[^A-Z\d]/gi, '') },
      'JAC': { regex: /^\d{10,}$/i, example: '3501100U2010', normalize: v => v.replace(/[^\d]/g, '') },
      'Haval': { regex: /\d{5}[A-Z]{4}\d[A-Z]$/i, example: '3501100XKZ16A', normalize: v => v.toUpperCase().replace(/[^\dA-Z]/gi, '') },
      'MG': { regex: /^(\d{5,8}|10\d{4})$/i, example: '10012345 or 102xxxxx', normalize: v => v.toUpperCase().replace(/[^\dA-Z]/gi, '') },
      'Landwind': { regex: /^JX\d{5,}$/i, example: 'JX12345', normalize: v => v.toUpperCase().replace(/[^\dA-Z]/gi, '') },
      'Proton': { regex: /^PW\d{6}$/i, example: 'PW123456', normalize: v => v.toUpperCase().replace(/[^\dA-Z]/gi, '') },
      'SAIC': { regex: /^\d{5,8}$/i, example: '10012345', normalize: v => v.replace(/[^\d]/g, '') },
      'DFSK': { regex: /^\d{4}\d{4}$/i, example: '10101234', normalize: v => v.replace(/[^\d]/g, '') },
      'Mahindra': { regex: /^0603AA\d{4}$/i, example: '0603AA1234', normalize: v => v.toUpperCase().replace(/[^\dA-Z]/gi, '') },
      'HINO': { regex: /^\d{5}-\d{5}$/i, example: '04465-37010', normalize: v => v.replace(/[^\d]/g, '').replace(/(\d{5})(\d{5})/, '$1-$2') },
      'JMC': { regex: /^JX\d{5,}$/i, example: 'JX12345', normalize: v => v.toUpperCase().replace(/[^\dA-Z]/gi, '') },
      // Other
      'Isuzu': { regex: /^(\d-\d{5}-\d{3}-\d|\d{8})$/i, example: '8-97123-456-0 or 897123456', normalize: v => v.replace(/[^\d]/g, '').replace(/^(\d)(\d{5})(\d{3})(\d)$/, '$1-$2-$3-$4') },
      'Izuzu': { regex: /^(\d-\d{5}-\d{3}-\d|\d{8})$/i, example: '8-97123-456-0 or 897123456', normalize: v => v.replace(/[^\d]/g, '').replace(/^(\d)(\d{5})(\d{3})(\d)$/, '$1-$2-$3-$4') },
      'Maserati': { regex: /^\d{9}$/i, example: '670031597', normalize: v => v.replace(/[^\d]/g, '') },
      'Peugeot': { regex: /^\d{10}$/i, example: '1611279180', normalize: v => v.replace(/[^\d]/g, '') },
      // VAG alias
      'VAG': { regex: /^\d{3} \d{3} \d{3} ?[A-Z]?$/i, example: '06H 121 026 DD', normalize: v => v.replace(/[^\dA-Z]/gi, '').replace(/(\d{3})(\d{3})(\d{3})([A-Z]?)/, '$1 $2 $3 $4').trim() },
    };
    const p = patterns[brand];
    if (!p) return { valid: true, normalized: partNo };
    if (p.regex.test(partNo)) return { valid: true, normalized: partNo };
    // Try normalization
    const normalized = p.normalize(partNo);
    if (p.regex.test(normalized)) return { valid: true, normalized, suggestion: normalized };
    // Try to suggest
    return { valid: false, expected: p.example, suggestion: normalized };
  }

  // Field enable logic
  const isAnyBrand = form.Brand === 'Any';
  const brandSelected = !!form.Brand;
  const partNoEnabled = isAnyBrand || (brandSelected && form.Brand !== '');
  const partTypeEnabled = isAnyBrand || (partNoEnabled && form.Part_no && form.Part_no.trim() !== '' && partNoFeedback && partNoFeedback.valid);
  const restEnabled = isAnyBrand ? !partNoDuplicate : (partNoEnabled && form.Part_no && !partNoDuplicate);

  // Validate on change
  useEffect(() => {
    if (!form.Brand || !form.Part_no) {
      setPartNoFeedback("");
      setPartNoDuplicate(false);
      return;
    }
    if (isAnyBrand) {
      setPartNoFeedback("");
      setPartNoDuplicate(false);
      return;
    }
    const result = validatePartNumber(form.Brand, form.Part_no);
    if (result.valid) {
      setPartNoFeedback({ text: result.suggestion ? `Normalized: ${result.suggestion}` : "Valid part number", color: 'green', valid: true });
    } else {
      setPartNoFeedback({ text: `Expected format: ${result.expected}. Example: ${result.expected}`, color: 'red', valid: false });
    }
  }, [form.Brand, form.Part_no, isAnyBrand]);

  useEffect(() => {
    const checkDuplicatePartNumber = async () => {
      if (!form.Part_no || !form.Part_type) {
        setPartNoDuplicate(false);
        return;
      }

      if (form.Brand !== 'Any' && (!partNoFeedback || !partNoFeedback.valid)) {
        setPartNoDuplicate(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const normalizedPartNo = form.Part_no.replace(/\s+/g, '');
        const response = await fetch(`${apiBaseURL}/api/parts/search?Part_no=${encodeURIComponent(normalizedPartNo)}&Part_type=${encodeURIComponent(form.Part_type)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setPartNoDuplicate(response.ok && data.success && Array.isArray(data.parts) && data.parts.length > 0);
      } catch (error) {
        console.error('Error checking duplicate part number:', error);
        setPartNoDuplicate(false);
      }
    };

    const timeoutId = setTimeout(() => {
      checkDuplicatePartNumber();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [form.Part_no, form.Part_type, form.Brand, partNoFeedback]);

  return (
    <div className="add-part-container">
      <h2>Add New Part</h2>
      <form onSubmit={handleSubmit} className="add-part-form">
        <div className="row">
          <div className="col">
            <label>Brand</label>
            <select
              name="Brand"
              value={form.Brand}
              onChange={handleChange}
              disabled={loading}
              required
            >
              <option value="">Select Brand</option>
              <option value="Any">Any</option>
              {manufacturers.map((m) => (
                <option key={m.Manufacturer || m} value={m.Manufacturer || m}>{m.Manufacturer || m}</option>
              ))}
            </select>
          </div>
          <div className="col">
            <label>Opening Date</label>
            <input 
              name="opening_date" 
              type="datetime-local" 
              value={form.opening_date} 
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Part Number</label>
            <input 
              name="Part_no" 
              placeholder="Part Number" 
              value={form.Part_no} 
              onChange={handleChange}
              disabled={!partNoEnabled || loading}
            />
            {partNoFeedback && !isAnyBrand && (
              <div className="feedback" style={{ color: partNoFeedback.color }}>{partNoFeedback.text}</div>
            )}
            {partNoDuplicate && (
              <div className="feedback" style={{ color: 'red' }}>Already present part # with same Part_type, do not entry.</div>
            )}
          </div>
          <div className="col" style={{ display: 'none' }}>
            <label>Part_type</label>
            <select
              name="Part_type"
              value={form.Part_type}
              onChange={handleChange}
              disabled={!partTypeEnabled || loading}
            >
              <option value="">Select Part_type</option>
              <option value="Genuine">Genuine</option>
              <option value="Recondition">Recondition</option>
              <option value="Used">Used</option>
              <option value="OEM">OEM</option>
              <option value="Recycled">Recycled</option>
            </select>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Part Name</label>
            <input 
              name="parts_name" 
              placeholder="Part Name" 
              value={form.parts_name} 
              onChange={handleChange} 
              required 
              disabled={!restEnabled || loading}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Description</label>
            <input 
              name="part_description" 
              placeholder="Description" 
              value={form.part_description} 
              onChange={handleChange}
              disabled={!restEnabled || loading}
            />
          </div>
        </div>
        <div className="row" style={{ display: 'none' }}>
          <div className="col">
            <label>Vendor</label>
            <input
              name="vendor_name"
              placeholder="Vendor name"
              value={form.vendor_name}
              onChange={(e) => {
                handleChange(e);
                setVendorSearchTerm(e.target.value);
              }}
              disabled={!restEnabled || loading}
            />
            {vendorSearchTerm && vendors.length > 0 && (
              <div className="vendor-suggestions">
                {vendors.map((vendor) => (
                  <div
                    key={vendor.VendorId}
                    className="vendor-suggestion"
                    onClick={() => handleVendorSelect(vendor)}
                  >
                    <strong>{vendor.company}</strong>
                    {vendor.phone1 ? ` - ${vendor.phone1}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="row" style={{ display: 'none' }}>
          <div className="col">
            <label>Opening Qty</label>
            <input 
              name="opening_qty" 
              type="number" 
              step="0.1" 
              placeholder="Opening Qty" 
              value={form.opening_qty} 
              onChange={handleChange}
              disabled={!restEnabled || loading}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Unit</label>
            <select
              name="unit"
              value={form.unit}
              onChange={handleChange}
              disabled={!restEnabled || loading}
              required
            >
              <option value="">Select Unit</option>
              <option value="pc.">pc.</option>
              <option value="l.">l.</option>
              <option value="grams">grams</option>
              <option value="ft.">ft.</option>
              <option value="fl.oz.">fl.oz.</option>
              <option value="gal.">gal.</option>
              <option value="in.">in.</option>
              <option value="lb.">lb.</option>
              <option value="oz.">oz.</option>
              <option value="pt.">pt.</option>
              <option value="qt.">qt.</option>
              <option value="sq.">sq.</option>
              <option value="mm.">mm.</option>
              <option value="cm.">cm.</option>
              <option value="m.">m.</option>
              <option value="kg.">kg.</option>
              <option value="m².">m².</option>
              <option value="cm³.">cm³.</option>
              <option value="m/s.">m/s.</option>
              <option value="km/h.">km/h.</option>
            </select>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>Limit</label>
            <input 
              name="Limit" 
              placeholder="Limit" 
              value={form.Limit} 
              onChange={handleChange}
              disabled={!restEnabled || loading}
            />
          </div>
          <div className="col">
            <label>Location</label>
            <input 
              name="Location" 
              placeholder="Location" 
              value={form.Location} 
              onChange={handleChange}
              disabled={!restEnabled || loading}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label>
              Dflag (Logical Delete)
              <input 
                name="Dflag" 
                type="checkbox" 
                checked={form.Dflag} 
                onChange={handleChange}
                disabled={!restEnabled || loading}
              />
            </label>
          </div>
        </div>
        <div className="row">
          <button type="submit" disabled={loading || !restEnabled} className="btn-main">{loading ? 'Adding...' : 'Add Part'}</button>
          <button type="button" onClick={() => navigate('/inventory/parts')} disabled={loading} className="btn-cancel">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default AddPart;
